import { Injectable, inject } from '@angular/core';
import { catchError, forkJoin, map, switchMap, throwError } from 'rxjs';
import {
  ChatMessagesService,
  ChatFilesService,
  ChatFilesGroupService,
  UploadFileGroupToChatDto,
} from '@src/api-messenger';
import { AlertService } from '@src/core/services';
import { convertFileToBlob } from '@src/utils';

import { MessageContentTypeEnum } from '../types';
import { AttachmentItem } from '../../attachment';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  private readonly chatMessagesService = inject(ChatMessagesService);
  private readonly chatFilesService = inject(ChatFilesService);
  private readonly chatFilesGroupService = inject(ChatFilesGroupService);
  private readonly alertService = inject(AlertService);

  deleteMessage(chatId: string, messageId: string) {
    return this.chatMessagesService.apiChatMessageDeletePost({ chatId, messageId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  sendTextMessage(chatId: string, messageText: string, replyMessageId?: string, replyMessageText?: string) {
    return this.chatMessagesService
      .apiChatMessageSendTextPost({ chatId, messageText, replyMessageId, replyMessageText })
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  editTextMessage(chatId: string, messageId: string, messageText: string) {
    return this.chatMessagesService.apiChatMessageEditTextPost({ chatId, messageId, messageText }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  downloadMessageFile(fileId: string, chatId: string) {
    return this.chatFilesService.apiChatFileDownloadPost({ fileId, chatId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  sendAttachmentsMessage(
    chatId: string,
    attachments: AttachmentItem[],
    contentType?: MessageContentTypeEnum,
    messageText?: string,
    replyMessageId?: string,
    replyMessageText?: string,
  ) {
    return this.startSendingMessageWithGroupFiles({
      chatId,
      messageText,
      contentType: contentType ?? MessageContentTypeEnum.File,
      replyMessageId,
      replyMessageText,
    })
      .pipe(
        map(res => {
          const messageId = res.id;

          return {
            messageId,
            files: attachments.map(attachment => {
              const file = attachment.file;
              const blob = convertFileToBlob(file);
              return this.sendFileForMessageGroup(chatId, messageId, blob, file.name, file.type, attachment.sortOrder);
            }),
          };
        }),
      )
      .pipe(
        switchMap(({ messageId, files }) => {
          return forkJoin(files).pipe(
            switchMap(() => {
              return this.endSendingMessageWithGroupFiles(chatId, messageId);
            }),
          );
        }),
      );
  }

  deleteChatFile(chatId: string, fileId: string) {
    return this.chatFilesService.apiChatFileDeletePost({ chatId, fileId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  sendReaction(chatId: string, replyMessageId: string, emodjiName: string) {
    return this.chatMessagesService.apiChatMessageSendReactionPost({ chatId, replyMessageId, emodjiName }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  readMessage(chatId: string, messageId: string) {
    this.chatMessagesService.apiChatMessageReadPost({ chatId, messageId }).subscribe();
  }

  startSendingMessageWithGroupFiles(data: UploadFileGroupToChatDto) {
    return this.chatFilesGroupService.apiChatFilesGroupPost(data).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  sendFileForMessageGroup(
    chatId: string,
    messageId: string,
    file: Blob,
    fileName: string,
    contentType: string,
    sortOrder: number,
  ) {
    return this.chatFilesGroupService
      .apiChatFilesGroupFilePost(chatId, messageId, file, fileName, contentType, sortOrder)
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      );
  }

  endSendingMessageWithGroupFiles(chatId: string, messageId: string) {
    return this.chatFilesGroupService.apiChatFilesGroupSentPost({ chatId, messageId }).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }
}
